/**
 * CNIC handling — validation and data minimisation.
 *
 * WHAT WE CAN AND CANNOT CLAIM
 * NADRA does not expose a public verification API, and the CNIC number carries
 * no published check digit. Therefore this module validates FORMAT ONLY. No
 * screen, no PDF and no report may imply that an identity has been verified —
 * saying "CNIC verified" when we have only checked that it is 13 digits would
 * be a false assurance printed on a legal document.
 *
 * MINIMISATION (gap D15)
 * The full CNIC is stored on the case document alone, which is readable only
 * inside that case's jurisdiction chain. Everywhere else — list views,
 * analytics, exports, and the repeat-offender index — uses a salted SHA-256
 * digest instead. The pepper lives in a Cloudflare Worker secret and never
 * reaches the client bundle.
 */
/** Format: 5 digits, 7 digits, 1 digit. Dashes optional on input. */
const CNIC_DIGITS = /^\d{13}$/;
const CNIC_FORMATTED = /^\d{5}-\d{7}-\d$/;
export function normaliseCnic(input) {
    return (input ?? '').replace(/[\s-]/g, '');
}
export function isValidCnicFormat(input) {
    const digits = normaliseCnic(input);
    if (!CNIC_DIGITS.test(digits))
        return false;
    // All-same-digit strings (0000000000000, 1111111111111) are placeholder junk
    // that turns up constantly in manual data entry.
    if (/^(\d)\1{12}$/.test(digits))
        return false;
    return true;
}
export function formatCnic(input) {
    const d = normaliseCnic(input);
    if (!CNIC_DIGITS.test(d))
        return input;
    return `${d.slice(0, 5)}-${d.slice(5, 12)}-${d.slice(12)}`;
}
export function isFormattedCnic(input) {
    return CNIC_FORMATTED.test(input);
}
/**
 * Last digit is odd for male, even for female. This is a documented convention,
 * not a guarantee — use it to pre-select a form field, never to override what a
 * person tells you about themselves.
 */
export function cnicGenderHint(input) {
    const d = normaliseCnic(input);
    if (!CNIC_DIGITS.test(d))
        return null;
    return Number(d[12]) % 2 === 1 ? 'male' : 'female';
}
/** Mask for display in any context wider than the owning case file. */
export function maskCnic(input) {
    const d = normaliseCnic(input);
    if (!CNIC_DIGITS.test(d))
        return '•••••-•••••••-•';
    return `${d.slice(0, 5)}-•••••••-${d.slice(12)}`;
}
/**
 * Salted digest used as the offender key in indexes, analytics and exports.
 *
 * Uses Web Crypto, which is available in browsers, Cloudflare Workers and
 * Node 20+ alike — so one implementation serves all three runtimes.
 *
 * @param cnic   raw or formatted CNIC
 * @param pepper server-held secret; NEVER ship this to the client
 */
export async function hashCnic(cnic, pepper) {
    const digits = normaliseCnic(cnic);
    if (!CNIC_DIGITS.test(digits)) {
        throw new Error('Cannot hash an invalid CNIC');
    }
    if (!pepper || pepper.length < 32) {
        // A short pepper makes the digest brute-forceable: the CNIC space is only
        // 10^13, so an unpeppered or weakly-peppered hash is reversible in hours.
        throw new Error('CNIC pepper must be at least 32 characters');
    }
    const data = new TextEncoder().encode(`${pepper}:${digits}`);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
/* -------------------------------------------------------------------------- */
/* Phone                                                                      */
/* -------------------------------------------------------------------------- */
/** Pakistani mobile: 03xxxxxxxxx locally, +923xxxxxxxxx internationally. */
export function normalisePhone(input) {
    const d = (input ?? '').replace(/[\s()-]/g, '');
    if (/^03\d{9}$/.test(d))
        return `+92${d.slice(1)}`;
    if (/^\+923\d{9}$/.test(d))
        return d;
    if (/^00923\d{9}$/.test(d))
        return `+${d.slice(2)}`;
    if (/^923\d{9}$/.test(d))
        return `+${d}`;
    return null;
}
export function isValidPhone(input) {
    return normalisePhone(input) !== null;
}
export function formatPhoneLocal(input) {
    const n = normalisePhone(input);
    if (!n)
        return input;
    return `0${n.slice(3)}`;
}
//# sourceMappingURL=cnic.js.map